// client/src/components/ItemDetails.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { buildSplatPath, paramFromSplatOrName } from '../utils/routeParams';
import { allowsVendorItemCodes } from '../utils/vendorItemCodes';
import '../App.css';

interface VendorCodeRow {
  vendorName: string;
  vendorItemCode: string;
}

interface Item {
  name: string;
  type: string;
  enabled?: boolean | number;
  vendorCodes?: VendorCodeRow[];
  canHaveProductionComponents?: boolean;
  depositItemName?: string | null;
  defaultLocationId?: number | null;
}

interface SimpleLocation {
  locationId: number;
  name: string;
  siteId: string;
}

interface InventoryBalanceRow {
  siteId: string;
  siteName: string;
  locationId: number;
  locationName: string;
  quantity: number;
  unit: string;
  group?: string;
}

interface ProductionComponent {
  id?: number;
  componentItemName: string;
  quantity: number;
  unit: string;
  isNonInventory?: number;
  sortOrder?: number;
}

const ITEM_TYPES = ['Grain', 'Hops', 'Yeast', 'Spirits', 'Packaging', 'Other'];

const ItemDetails: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const decodedName = paramFromSplatOrName(params, 'name');
  const isNew = decodedName === 'new' || params.name === 'new';
  const [itemDetails, setItemDetails] = useState<Item | null>(null);
  const [editing, setEditing] = useState(isNew);
  const [editedItem, setEditedItem] = useState<Item>({ name: '', type: 'Other', vendorCodes: [] });
  const [vendors, setVendors] = useState<{ name: string }[]>([]);
  const [allLocations, setAllLocations] = useState<SimpleLocation[]>([]);
  const [productionError, setProductionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState<'inventory' | 'components'>('inventory');
  const [balances, setBalances] = useState<{
    totalOnHand: number;
    unit: string;
    rows: InventoryBalanceRow[];
    taproomRows: InventoryBalanceRow[];
    finishedGood?: boolean;
  } | null>(null);
  const [components, setComponents] = useState<ProductionComponent[]>([]);
  const [editingBom, setEditingBom] = useState(false);
  const [bomDraft, setBomDraft] = useState<ProductionComponent[]>([]);
  const [tabError, setTabError] = useState<string | null>(null);
  const [tabLoading, setTabLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [vendorsRes, locsRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/vendors`),
          apiFetch(`${API_BASE_URL}/api/locations`),
        ]);
        if (vendorsRes.ok) {
          const data = await vendorsRes.json();
          setVendors(Array.isArray(data) ? data : data.vendors || []);
        }
        if (locsRes.ok) {
          const data = await locsRes.json();
          setAllLocations(Array.isArray(data) ? data : []);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const fetchItemDetails = useCallback(async () => {
    setLoading(true);
    setProductionError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/items/${encodeURIComponent(decodedName)}`,
        { allowHttpError: true }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP error! status: ${res.status}, ${text.slice(0, 80)}`);
      }
      const data = await res.json();
      const item: Item = {
        name: data.name,
        type: data.type || 'Other',
        enabled: data.enabled,
        vendorCodes: (data.vendorCodes || []).map((c: any) => ({
          vendorName: c.vendorName,
          vendorItemCode: c.vendorItemCode,
        })),
        canHaveProductionComponents: !!data.canHaveProductionComponents,
        depositItemName: data.depositItemName || null,
        defaultLocationId:
          data.defaultLocationId != null && data.defaultLocationId !== ''
            ? Number(data.defaultLocationId)
            : null,
      };
      if (!item.name) {
        throw new Error('Item response missing name');
      }
      setItemDetails(item);
      setEditedItem(item);
    } catch (err: any) {
      console.error('Fetch item error:', err);
      setItemDetails(null);
      setProductionError('Item not found / failed to load');
    } finally {
      setLoading(false);
    }
  }, [decodedName]);

  useEffect(() => {
    if (!isNew && decodedName) {
      void fetchItemDetails();
    } else if (!isNew && !decodedName) {
      setLoading(false);
      setItemDetails(null);
      setProductionError('Item not found / failed to load');
    }
  }, [decodedName, isNew, fetchItemDetails]);

  const loadInventoryTab = useCallback(async () => {
    if (!itemDetails?.name) return;
    setTabLoading(true);
    setTabError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/items/${encodeURIComponent(itemDetails.name)}/inventory-balances`,
        { allowHttpError: true }
      );
      if (!res.ok) throw new Error(`Failed to load inventory (${res.status})`);
      const data = await res.json();
      setBalances({
        totalOnHand: Number(data.totalOnHand) || 0,
        unit: data.unit || 'ea',
        rows: Array.isArray(data.rows) ? data.rows : [],
        taproomRows: Array.isArray(data.taproomRows) ? data.taproomRows : [],
        finishedGood: !!data.finishedGood,
      });
    } catch (err: any) {
      setTabError(err.message || 'Failed to load inventory');
      setBalances(null);
    } finally {
      setTabLoading(false);
    }
  }, [itemDetails?.name]);

  const loadComponentsTab = useCallback(async () => {
    if (!itemDetails?.name) return;
    setTabLoading(true);
    setTabError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/items/${encodeURIComponent(itemDetails.name)}/production-components`,
        { allowHttpError: true }
      );
      if (!res.ok) throw new Error(`Failed to load components (${res.status})`);
      const data = await res.json();
      const list = (data.components || []).map((c: any) => ({
        id: c.id,
        componentItemName: c.componentItemName,
        quantity: Number(c.quantity),
        unit: c.unit || 'ea',
        isNonInventory: Number(c.isNonInventory) ? 1 : 0,
        sortOrder: c.sortOrder,
      }));
      setComponents(list);
      setBomDraft(list);
      setItemDetails((prev) =>
        prev
          ? {
              ...prev,
              canHaveProductionComponents: data.canEdit !== false,
              depositItemName: data.depositItemName || prev.depositItemName,
            }
          : prev
      );
    } catch (err: any) {
      setTabError(err.message || 'Failed to load production components');
    } finally {
      setTabLoading(false);
    }
  }, [itemDetails?.name]);

  useEffect(() => {
    if (!itemDetails?.name || isNew || editing) return;
    if (activeTab === 'inventory') void loadInventoryTab();
    else void loadComponentsTab();
  }, [activeTab, itemDetails?.name, isNew, editing, loadInventoryTab, loadComponentsTab]);

  const handleSave = async () => {
    if (!editedItem.name) {
      setProductionError('Item name is required');
      return;
    }
    try {
      const method = !isNew ? 'PUT' : 'POST';
      const url = `${API_BASE_URL}/api/items`;
      const showVendorCodes = allowsVendorItemCodes(editedItem.type, editedItem.name);
      const vendorCodes = showVendorCodes
        ? (editedItem.vendorCodes || [])
            .map((c) => ({
              vendorName: String(c.vendorName || '').trim(),
              vendorItemCode: String(c.vendorItemCode || '').trim().toUpperCase(),
            }))
            .filter((c) => c.vendorName && c.vendorItemCode)
        : [];
      const locOverride =
        editedItem.defaultLocationId != null && editedItem.defaultLocationId !== ('' as any)
          ? Number(editedItem.defaultLocationId)
          : null;
      const body =
        method === 'PUT'
          ? {
              oldName: decodedName,
              newName: editedItem.name,
              type: editedItem.type || 'Other',
              defaultLocationId: locOverride,
              ...(showVendorCodes ? { vendorCodes } : {}),
            }
          : {
              name: editedItem.name,
              type: editedItem.type || 'Other',
              enabled: true,
              defaultLocationId: locOverride,
              ...(showVendorCodes ? { vendorCodes } : {}),
            };
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        allowHttpError: true,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP error! status: ${res.status}, ${text.slice(0, 80)}`);
      }
      const saved = await res.json();
      const item: Item = {
        name: saved.name,
        type: saved.type || 'Other',
        enabled: saved.enabled,
        vendorCodes: (saved.vendorCodes || vendorCodes).map((c: any) => ({
          vendorName: c.vendorName,
          vendorItemCode: c.vendorItemCode,
        })),
        canHaveProductionComponents: !!saved.canHaveProductionComponents,
        depositItemName: saved.depositItemName || null,
        defaultLocationId:
          saved.defaultLocationId != null && saved.defaultLocationId !== ''
            ? Number(saved.defaultLocationId)
            : locOverride,
      };
      setItemDetails(item);
      setEditedItem(item);
      setEditing(false);
      setProductionError(null);
      navigate(buildSplatPath('/items', item.name));
    } catch (err: any) {
      console.error('Save item error:', err);
      setProductionError('Failed to save item: ' + err.message);
    }
  };

  const handleCancel = () => {
    if (!isNew && itemDetails) {
      setEditing(false);
      setEditedItem(itemDetails);
    } else {
      navigate('/items');
    }
  };

  const updateVendorCode = (index: number, patch: Partial<VendorCodeRow>) => {
    const rows = [...(editedItem.vendorCodes || [])];
    rows[index] = { ...rows[index], ...patch };
    setEditedItem({ ...editedItem, vendorCodes: rows });
  };

  const saveBom = async () => {
    if (!itemDetails?.name) return;
    setTabError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/items/${encodeURIComponent(itemDetails.name)}/production-components`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            components: bomDraft.map((c, idx) => ({
              componentItemName: c.componentItemName,
              quantity: Number(c.quantity),
              unit: c.unit || 'ea',
              isNonInventory: /\bdeposit\b/i.test(c.componentItemName) ? 1 : c.isNonInventory || 0,
              sortOrder: idx,
            })),
          }),
          allowHttpError: true,
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          (body && typeof body.error === 'string' && body.error) ||
          (body && typeof body.message === 'string' && body.message) ||
          `Save failed (${res.status})`;
        throw new Error(message);
      }
      const data = await res.json();
      const list = (data.components || []).map((c: any) => ({
        componentItemName: c.componentItemName,
        quantity: Number(c.quantity),
        unit: c.unit || 'ea',
        isNonInventory: Number(c.isNonInventory) ? 1 : 0,
      }));
      setComponents(list);
      setBomDraft(list);
      setEditingBom(false);
    } catch (err: any) {
      setTabError(err.message || 'Failed to save production components');
    }
  };

  if (loading) return <div className="page-container">Loading...</div>;

  if (!isNew && !itemDetails) {
    return (
      <div className="page-container" style={{ padding: '20px', maxWidth: '720px', margin: '20px auto' }}>
        <h2 className="detail-heading app-header" style={{ fontSize: '28px', marginBottom: '20px' }}>
          Item Details
        </h2>
        <p className="error">{productionError || 'Item not found / failed to load'}</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => void fetchItemDetails()} style={btnAccent}>
            Retry
          </button>
          <button type="button" onClick={() => navigate('/items')} style={btnDark}>
            Back to Items
          </button>
        </div>
      </div>
    );
  }

  const activeType = editing ? editedItem.type : itemDetails?.type;
  const activeName = editing ? editedItem.name : itemDetails?.name;
  const showVendorCodes = allowsVendorItemCodes(activeType, activeName);
  const codes = editing ? editedItem.vendorCodes || [] : itemDetails?.vendorCodes || [];
  const showBomTab = !isNew && (itemDetails?.canHaveProductionComponents || /Keg\s*-|Case\s*-/i.test(itemDetails?.name || ''));

  return (
    <div className="page-container" style={{ padding: '20px', maxWidth: '960px', margin: '20px auto' }}>
      <h2 className="detail-heading app-header" style={{ fontSize: '28px', marginBottom: '20px' }}>
        {isNew ? 'Add New Item' : itemDetails?.name || 'Item Details'}
      </h2>
      {productionError && <p className="error">{productionError}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <label className="detail-label" style={{ fontSize: '18px' }}>
          Name:
          {editing ? (
            <input
              type="text"
              value={editedItem.name}
              onChange={(e) => setEditedItem({ ...editedItem, name: e.target.value })}
              style={fieldStyle}
            />
          ) : (
            <span className="detail-value" style={{ marginLeft: '10px' }}>
              {itemDetails?.name}
            </span>
          )}
        </label>
        <label className="detail-label" style={{ fontSize: '18px' }}>
          Type:
          {editing ? (
            <select
              value={editedItem.type || 'Other'}
              onChange={(e) => setEditedItem({ ...editedItem, type: e.target.value })}
              style={fieldStyle}
            >
              {ITEM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          ) : (
            <span className="detail-value" style={{ marginLeft: '10px' }}>
              {itemDetails?.type || 'Other'}
            </span>
          )}
        </label>
        <label className="detail-label" style={{ fontSize: '18px' }}>
          Default storage location override:
          {editing ? (
            <select
              value={editedItem.defaultLocationId != null ? String(editedItem.defaultLocationId) : ''}
              onChange={(e) =>
                setEditedItem({
                  ...editedItem,
                  defaultLocationId: e.target.value ? Number(e.target.value) : null,
                })
              }
              style={fieldStyle}
            >
              <option value="">Use site category default</option>
              {allLocations.map((l) => (
                <option key={l.locationId} value={String(l.locationId)}>
                  {l.siteId} — {l.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="detail-value" style={{ marginLeft: '10px' }}>
              {itemDetails?.defaultLocationId
                ? allLocations.find((l) => l.locationId === itemDetails.defaultLocationId)?.name ||
                  `#${itemDetails.defaultLocationId}`
                : 'Site category default'}
            </span>
          )}
        </label>

        {showVendorCodes && (
          <div style={{ marginTop: '8px' }}>
            <div className="detail-label" style={{ fontSize: '18px', marginBottom: '8px' }}>
              Vendor product numbers
            </div>
            <p className="detail-muted" style={{ fontSize: '13px', marginTop: 0 }}>
              Codes used on vendor invoices (e.g. Country Malt Group <code>GB-HONY</code>).
            </p>
            {codes.length === 0 && !editing && <p className="detail-value">No vendor codes yet.</p>}
            {codes.map((row, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: editing ? '1fr 1fr auto' : '1fr 1fr',
                  gap: '8px',
                  marginBottom: '8px',
                  alignItems: 'center',
                }}
              >
                {editing ? (
                  <>
                    <select
                      value={row.vendorName}
                      onChange={(e) => updateVendorCode(idx, { vendorName: e.target.value })}
                      style={fieldStyle}
                    >
                      <option value="">Vendor…</option>
                      {vendors.map((v) => (
                        <option key={v.name} value={v.name}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                    <input
                      value={row.vendorItemCode}
                      placeholder="Vendor item code"
                      onChange={(e) =>
                        updateVendorCode(idx, { vendorItemCode: e.target.value.toUpperCase() })
                      }
                      style={fieldStyle}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setEditedItem({
                          ...editedItem,
                          vendorCodes: (editedItem.vendorCodes || []).filter((_, i) => i !== idx),
                        })
                      }
                      style={btnDark}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <span className="detail-value">{row.vendorName}</span>
                    <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {row.vendorItemCode}
                    </span>
                  </>
                )}
              </div>
            ))}
            {editing && (
              <button
                type="button"
                onClick={() =>
                  setEditedItem({
                    ...editedItem,
                    vendorCodes: [
                      ...(editedItem.vendorCodes || []),
                      { vendorName: '', vendorItemCode: '' },
                    ],
                  })
                }
                style={{ ...btnAccent, backgroundColor: '#2196F3' }}
              >
                Add vendor code
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button onClick={() => void handleSave()} style={btnAccent}>
            Save
          </button>
          <button onClick={handleCancel} style={{ ...btnDark, marginLeft: 10 }}>
            Cancel
          </button>
        </div>
      )}
      {!editing && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button onClick={() => setEditing(true)} style={btnAccent}>
            Edit
          </button>
        </div>
      )}

      {!isNew && !editing && itemDetails && (
        <>
          <div className="vendor-tabs" style={{ marginTop: 28 }}>
            <button
              type="button"
              className={activeTab === 'inventory' ? 'active' : ''}
              onClick={() => setActiveTab('inventory')}
            >
              Inventory
            </button>
            {showBomTab && (
              <button
                type="button"
                className={activeTab === 'components' ? 'active' : ''}
                onClick={() => setActiveTab('components')}
              >
                Production Components
              </button>
            )}
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as 'inventory' | 'components')}
              className="form-control"
            >
              <option value="inventory">Inventory</option>
              {showBomTab && <option value="components">Production Components</option>}
            </select>
          </div>

          {tabError && <p className="error">{tabError}</p>}
          {tabLoading && <p className="detail-muted">Loading…</p>}

          {activeTab === 'inventory' && balances && !tabLoading && (
            <div>
              <p className="detail-value" style={{ fontWeight: 600, marginBottom: 12 }}>
                Total on hand: {balances.totalOnHand} {balances.unit}
              </p>
              {itemDetails?.type === 'Spirits-Packaged' && (
                <p className="detail-muted" style={{ fontSize: 13, marginBottom: 12 }} data-testid="item-spirits-bottles-note">
                  Distillery bottle counts are kept in{' '}
                  <Link to="/spirits/bottles">Spirits Bottles</Link>, not on this tab.
                </p>
              )}
              <div className="inventory-table-container">
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Location</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balances.rows.map((row) => (
                      <tr key={`onhand-${row.siteId}-${row.locationId}`}>
                        <td className="detail-value">{row.siteName}</td>
                        <td className="detail-value">{row.locationName}</td>
                        <td className="detail-value">
                          {row.quantity} {row.unit}
                        </td>
                      </tr>
                    ))}
                    {!balances.rows.length && (
                      <tr>
                        <td className="detail-muted" colSpan={3}>
                          No on-hand locations
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {balances.finishedGood && balances.taproomRows.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <p className="detail-value" style={{ fontWeight: 600, marginBottom: 8 }}>
                    Taproom (sold, tax paid)
                  </p>
                  <p className="detail-muted" style={{ fontSize: 13, marginBottom: 12 }}>
                    Beer at the taproom is considered sold and is not included in on-hand totals.
                  </p>
                  <div className="inventory-table-container">
                    <table className="inventory-table">
                      <thead>
                        <tr>
                          <th>Site</th>
                          <th>Location</th>
                          <th>Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {balances.taproomRows.map((row) => (
                          <tr key={`taproom-${row.siteId}-${row.locationId}`}>
                            <td className="detail-value">{row.siteName}</td>
                            <td className="detail-value">{row.locationName}</td>
                            <td className="detail-value">
                              {row.quantity} {row.unit}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'components' && showBomTab && !tabLoading && (
            <div>
              <p className="detail-muted" style={{ fontSize: 13 }}>
                Bill of materials consumed when this item is packaged. Deposit lines are not deducted
                from inventory.
              </p>
              {!editingBom ? (
                <>
                  <table className="inventory-table">
                    <thead>
                      <tr>
                        <th>Component Item</th>
                        <th>Quantity</th>
                        <th>UOM</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {components.length === 0 && (
                        <tr>
                          <td colSpan={4} className="detail-muted">
                            No production components yet. Click Edit to add, or run admin
                            mode=production-components.
                          </td>
                        </tr>
                      )}
                      {components.map((c, idx) => (
                        <tr key={idx}>
                          <td className="detail-value">{c.componentItemName}</td>
                          <td className="detail-value">{c.quantity}</td>
                          <td className="detail-value">{c.unit}</td>
                          <td className="detail-muted">
                            {c.isNonInventory ? 'Deposit / non-inventory' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    type="button"
                    onClick={() => {
                      setBomDraft(components.length ? components : [{ componentItemName: '', quantity: 1, unit: 'ea' }]);
                      setEditingBom(true);
                    }}
                    style={{ ...btnAccent, marginTop: 12 }}
                  >
                    Edit components
                  </button>
                </>
              ) : (
                <>
                  {bomDraft.map((c, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr auto',
                        gap: 8,
                        marginBottom: 8,
                        alignItems: 'center',
                      }}
                    >
                      <input
                        value={c.componentItemName}
                        placeholder="Component item"
                        onChange={(e) => {
                          const next = [...bomDraft];
                          next[idx] = { ...next[idx], componentItemName: e.target.value };
                          setBomDraft(next);
                        }}
                        style={fieldStyle}
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={c.quantity}
                        onChange={(e) => {
                          const next = [...bomDraft];
                          next[idx] = { ...next[idx], quantity: Number(e.target.value) };
                          setBomDraft(next);
                        }}
                        style={fieldStyle}
                      />
                      <input
                        value={c.unit}
                        onChange={(e) => {
                          const next = [...bomDraft];
                          next[idx] = { ...next[idx], unit: e.target.value };
                          setBomDraft(next);
                        }}
                        style={fieldStyle}
                      />
                      <button
                        type="button"
                        onClick={() => setBomDraft(bomDraft.filter((_, i) => i !== idx))}
                        style={btnDark}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() =>
                        setBomDraft([
                          ...bomDraft,
                          { componentItemName: '', quantity: 1, unit: 'ea', isNonInventory: 0 },
                        ])
                      }
                      style={{ ...btnAccent, backgroundColor: '#2196F3' }}
                    >
                      Add component
                    </button>
                    <button type="button" onClick={() => void saveBom()} style={btnAccent}>
                      Save components
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBomDraft(components);
                        setEditingBom(false);
                      }}
                      style={btnDark}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  fontSize: '16px',
  borderRadius: '4px',
  border: '1px solid #000000',
  marginTop: '5px',
  color: 'var(--page-text)',
  backgroundColor: 'var(--page-bg)',
};

const btnAccent: React.CSSProperties = {
  backgroundColor: 'var(--page-accent)',
  color: 'var(--page-accent-text)',
  padding: '10px 20px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '16px',
  cursor: 'pointer',
};

const btnDark: React.CSSProperties = {
  backgroundColor: '#000000',
  color: 'var(--menu-accent)',
  padding: '10px 20px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '16px',
  cursor: 'pointer',
};

export default ItemDetails;
